  /**
   * Ungroups a SelectionGroup layer in the creative unit, moving its sublayers to the root and removing the group.
   * @param creativeUnit The creative unit to modify.
   * @param selectionGroupId The ID of the SelectionGroup to ungroup.
   * @returns The updated creative unit with the group removed and its layers promoted to root.
   */

  /**
   * Ungroups a SelectionGroup layer in the creative unit, moving its sublayers to the root and removing the group.
   * @param creativeUnit The creative unit to modify.
   * @param selectionGroupId The ID of the SelectionGroup to ungroup.
   * @returns The updated creative unit with the group removed and its layers promoted to root.
   */
  import { cloneDeep, isEqual, merge } from 'lodash';
import {
	CreativeUnitDimensionsUnit,
	CreativeUnitDto,
	CreativeUnitWithCustomizationsDto
} from '../models/creative-unit/creative-unit.dto';
import {
	CustomizationDto,
	CustomizationItemDto,
	CustomizationType,
	getCustomizationClassByType
} from '../models/customization/customization.dto';
  import { LayerDto, LayerType } from '../models/layer/layer.dto';
  import { CustomizationUtils } from './customization.utils';
  import { LayersUtils } from './layers.utils';
  import { Evaluate } from '../../_core/utils/utils.evaluate';
  import { MergeTags } from '../../_core/utils/utils.merge-tags';
  import { LayerOptimizationsDto } from '../models/layer/layer-optimization.dto';
  import { PublicBusiness } from '../../business/business.entity';

  import { Dimension, Dimensions as DimensionUtils } from '../../_core/utils/utils.dimensions';
  import { MergePropertiesUtils } from './merge-properties.utils';
  import { CreativeUnitListingDto } from '../models/creative-unit/creative-unit-listing.dto';
  import { CustomizationOptionDto, CustomizationOptionMappingDto, CustomizationOverrideDto } from '../models/customization/customization-option.dto';
  import { CreativeUnitPackageDto } from '../models/creative-unit-package.dto';
  import { StylesConfigDto } from "../models/styles.dto";
  import { ObjectUtils } from '../../_core/utils/utils.object';
  import { ProjectCartItemGroupDto } from '../../cart/models/project-cart-item-group.dto';

  export class CreativeUnitConversionConfig {
	// The Cloudinary ID of the bucket to use for image optimizations.
	cloudinaryBucketId: string;

	// The url to the VML Transformer engine.
	taskerEngineUrl?: string;

	// The url to the API server (for auto target crop proxy).
	apiUrl?: string;

	// Business data to use for merge tags.
	business: PublicBusiness;

	// The method to use to convert SASS to CSS.
	sassConversionMethod: Function;

	// Preview Mode
	previewMode?: boolean;

	// The active group to use for merge tags.
	activeGroup?: ProjectCartItemGroupDto

	// Seed for randomization
	seed?: number;

	// The package to use for merge tags (provides package-level variables and config).
	package?: CreativeUnitPackageDto;
  /**
   * Groups selected layers into a new SelectionGroup layer within the creative unit.
   * The selected layers are moved under the new group's layers property and removed from the root.
   * The group is added at the position of the first selected layer.
   * @param creativeUnit The creative unit to modify.
   * @param selectedLayerIds The IDs of the layers to group.
   * @param groupLabel Optional label for the new group.
   * @returns The updated creative unit with the new SelectionGroup.
   */
  public static groupLayersIntoSelectionGroup(
	creativeUnit: CreativeUnitWithCustomizationsDto,
	selectedLayerIds: string[],
	groupLabel?: string
  ): CreativeUnitWithCustomizationsDto {
	if (!selectedLayerIds?.length) return creativeUnit;

	// Find all selected layers recursively
	const allLayers = LayersUtils.findAllLayers(creativeUnit.layers, layer => selectedLayerIds.includes(layer.id));
	if (!allLayers.length) return creativeUnit;

	// Find the parent and index of the first selected layer
	const firstSelectedId = selectedLayerIds[0];
	let parentLayers = creativeUnit.layers;
	let insertIndex = parentLayers.findIndex(l => l.id === firstSelectedId);
	let found = false;

	// Recursively search for the parent and index
	function findParentAndIndex(layers: LayerDto[]): { parent: LayerDto[]; index: number } | null {
	  for (let i = 0; i < layers.length; i++) {
		if (layers[i].id === firstSelectedId) {
		  return { parent: layers, index: i };
		}
		if (layers[i].layers) {
		  const result = findParentAndIndex(layers[i].layers);
		  if (result) return result;
		}
	  }
	  return null;
	}
	const parentResult = findParentAndIndex(creativeUnit.layers);
	if (parentResult) {
	  parentLayers = parentResult.parent;
	  insertIndex = parentResult.index;
	}

	// Remove all selected layers recursively
	const newLayers = LayersUtils.filterAllLayers(creativeUnit.layers, layer => !selectedLayerIds.includes(layer.id));

	// Create the SelectionGroup layer
	const selectionGroup: LayerDto = {
	  id: 'selection-group-' + Date.now(),
	  type: LayerType.SELECTION_GROUP,
	  label: groupLabel || 'Selection Group',
	  creativeUnitId: creativeUnit.id,
	  layers: allLayers,
	};

	// Insert the group at the correct position in the parent's layers array
	if (insertIndex >= 0) {
	  parentLayers.splice(insertIndex, 0, selectionGroup);
	} else {
	  parentLayers.push(selectionGroup);
	}

	return {
	  ...creativeUnit,
	  layers: newLayers
	};
  }

}

export class CreativeUnitUtils {
	public static resolveTransformUnitTarget(
		pkg: CreativeUnitPackageDto,
		unit: CreativeUnitWithCustomizationsDto,
		options?: {
			dimensionCustomizationId?: string;
			dimensionCandidateIds?: string[];
			dimensionSize?: { width: number | string; height: number | string };
			dimensionPriority?: number;
		}
	): { creativeUnitId: string; source: 'dimensions' | 'transform' } | undefined {
		if (!pkg?.creativeUnits?.length || !unit) {
			return undefined;
		}

		const flattenedCustomizations = CustomizationUtils.findAllCustomizations(unit.customizations) || [];
		const candidateMap = new Map<string, { creativeUnitId: string; priority: number; order: number; source: 'dimensions' | 'transform' }>();
		let candidateOrder = 0;

		const addCandidate = (creativeUnitId: string, priority: number, source: 'dimensions' | 'transform') => {
			if (!creativeUnitId) {
				return;
			}

			const existsInPackage = pkg.creativeUnits.find(cu => cu.id === creativeUnitId);
			if (!existsInPackage) {
				return;
			}

			const order = candidateOrder++;
			const existing = candidateMap.get(creativeUnitId);
			if (!existing || priority < existing.priority || (priority === existing.priority && order < existing.order)) {
				candidateMap.set(creativeUnitId, { creativeUnitId, priority, order, source });
			}
		};

		flattenedCustomizations.forEach((customization, index) => {
			const mappingTransform = customization?.mappings?.transformUnit;
			if (mappingTransform?.enabled && mappingTransform.creativeUnitId) {
				addCandidate(mappingTransform.creativeUnitId, mappingTransform.priority ?? index, 'transform');
			}

			const value = customization?.value as CustomizationOptionDto;
			const optionTransform = value?.transformUnit;
			if (optionTransform?.enabled && optionTransform.creativeUnitId) {
				addCandidate(optionTransform.creativeUnitId, optionTransform.priority ?? index, 'transform');
			}
		});

		if (
			options?.dimensionCandidateIds?.length &&
			options.dimensionSize?.width &&
			options.dimensionSize?.height
		) {
			const dimensionUnits = pkg.creativeUnits.filter(cu => options.dimensionCandidateIds.includes(cu.id));
			if (dimensionUnits?.length) {
				const similarUnit = this.getMostSimilarCreativeUnit(
					dimensionUnits,
					String(options.dimensionSize.width),
					String(options.dimensionSize.height)
				);

				if (similarUnit) {
					let priority = options.dimensionPriority ?? Number.MAX_SAFE_INTEGER;
					if (options.dimensionCustomizationId) {
						const dimensionIndex = flattenedCustomizations.findIndex(c => c.id === options.dimensionCustomizationId);
						if (dimensionIndex >= 0) {
							priority = Math.min(priority, dimensionIndex);
						}
					}

					addCandidate(similarUnit.id, priority, 'dimensions');
				}
			}
		}

		if (!candidateMap.size) {
			return undefined;
		}

		const [best] = [...candidateMap.values()].sort((a, b) => {
			if (a.priority === b.priority) {
				return a.order - b.order;
			}
			return a.priority - b.priority;
		});
		return best ? { creativeUnitId: best.creativeUnitId, source: best.source } : undefined;
	}

	/**
	 * Convert an ad unit with customization to a regular ad unit.
	 * Applies all the customizations to the root and layers.
	 * Then it removes all the customizations data.
	 */
	public static async convertToCreativeUnit(
		unit: CreativeUnitWithCustomizationsDto,
		config: CreativeUnitConversionConfig
	): Promise<CreativeUnitDto> {
		// console.log('Converting to Creative Unit', unit, config);

		let newUnit = { ...unit };

		const customizations = CustomizationUtils.findAllCustomizations(unit.customizations);
		// Delete the customizations keys on the root and in layers.
		// We have to force the type here because we are removing the customizations keys.
		// that shouldn't be there, but we don't want to delete them because they are read-only.
		let visibleCustomizations = CustomizationUtils.getAllVisibleCustomizations(
			customizations,
			{
				...unit,
				customizations: customizations
			},
			undefined,
			undefined,
			config.activeGroup
		);

		// console.log('converToCreativeUnit', newUnit, visibleCustomizations, unit);

		// Update all the layers with the customizations active settings.
		newUnit = {
			...this.updateCreativeUnitWithCustomizationsValues(unit, visibleCustomizations)
		};

		// console.log('Updated Unit With Customization Values', newUnit);

		// Build a state object of customizations and layers to check against.  Convert the arrays into objects with id as the key.
		let state: any = MergePropertiesUtils.getMergeableProperties(
			config.package, // Pass package to include package-level variables and config
			config.business,
			{
				...newUnit,
				customizations: visibleCustomizations
			},
			config.activeGroup
		);
		// console.log('Merge Tags', state);

		// Add preview mode to the state if it exists.  Useful for scripts to know if they are in preview mode.
		if (config.previewMode) {
			state = {
				...state,
				previewMode: true
			}
		}

		// Alter dimensions based on merge state.
		newUnit = {
			...newUnit,
			dimensions: {
				width: Number(MergeTags.applyMergeTagsToString(newUnit?.dimensions?.width?.toString(), state)),
				height: Number(MergeTags.applyMergeTagsToString(newUnit?.dimensions?.height?.toString(), state)),
				ppi: Number(MergeTags.applyMergeTagsToString(newUnit?.dimensions?.ppi?.toString(), state)),
				defaultDisplayUnit: newUnit?.dimensions?.defaultDisplayUnit
			},
			animationConfig: {
				...newUnit.animationConfig,
				totalDuration: MergeTags.applyMergeTagsToString(newUnit?.animationConfig?.totalDuration?.toString(), state)
			},
			background: {
				...newUnit.background,
				color: MergeTags.applyMergeTagsToString(newUnit?.background?.color, state)
			}
		};

		// Update our state with any new dimensions.
		// state = CreativeUnitUtils.getCreativeUnitMergeableProperties(
		// 	{
		// 		...newUnit,
		// 		customizations: visibleCustomizations
		// 	},
		// 	config.business
		// );




		// Only include layers that are visible.
		let newLayers = LayersUtils.findAllVisibleLayers(newUnit.layers, state);
		// console.log('New Layers', newLayers);

		// Remove extraneous config from the layers.
                newLayers = LayersUtils.mapAllLayers(newLayers, layer => {

			// console.log('Map Layer', layer, state);
			let mergeTaggedLayer;
			try {
				mergeTaggedLayer = JSON.parse(MergeTags.applyMergeTagsToString(JSON.stringify(layer), state));
			} catch (e) {
				console.warn('Unable to parse merge tags', e);
				mergeTaggedLayer = layer;
			}

			let newLayer = {
				...mergeTaggedLayer,
				customizations: undefined,
				visibilityConditions: undefined
			};

			// If the layer has an 'assetPath' property, replace the Asset property of this layer's type.
			if (layer.assetPath) {
				newLayer = {
					...newLayer,
					[layer.type]: {
						assetPath: mergeTaggedLayer.assetPath
					},
					assetPath: undefined
				};
				// console.log('Asset Path', newLayer);
			}

			// Add in image optimizations
			if (newLayer.optimizations || newLayer.previewOptimizations) {
				// console.log('Optimizing Layer', layer.optimizations, layer.previewOptimizations, config.previewMode);
				if (config.previewMode) {
					// Apply preview optimizations on top of media optimizations.
					// console.log('Applying Preview Optimizations', layer.previewOptimizations, layer.optimizations);
					// console.log('Merged Optimizations', merge(layer.optimizations, layer.previewOptimizations));
					let newOptimizations = merge(newLayer.optimizations, newLayer.previewOptimizations);

					// Add in a max resolution if one doesn't already exist.
					if (!newOptimizations?.resize?.maxResolution) {
						newOptimizations = {
							...newOptimizations,
							resize: {
								...newOptimizations?.resize,
								maxResolution: 2000
							}
						}
					}

					newLayer = {
						...newLayer,
						optimizations: newOptimizations
					};
				}

				newLayer = LayerOptimizationsDto.optimizeLayer(newLayer, config.cloudinaryBucketId, config.taskerEngineUrl, config.apiUrl, config.seed);
				// console.log('Optimized Layer', newLayer);
			}

                        return newLayer;
                });

                // If animations are disabled for this unit, strip timing and animation data
                if (newUnit.animationConfig?.disableAnimations) {
                        newLayers = LayersUtils.mapAllLayers(newLayers, layer => {
                                const { timing, animation, ...rest } = layer as any;
                                return { ...rest } as any;
                        });

                        newUnit = {
                                ...newUnit,
                                animationConfig: {
                                        ...newUnit.animationConfig,
                                        totalDuration: undefined
                                }
                        };
                }

		// Try to parse our styles from SASS to CSS.  The parent needs to pass in their own SASS conversion method.
		if (unit.styles) {
			// console.log('Unit Styles', unit.styles);
			if (Array.isArray(unit.styles)) {
				for (let i = 0; i < unit.styles.length; i++) {
					try {
						// console.log('Converting SASS', unit.styles[i]?.styles);
						let style = MergeTags.applyMergeTagsToString(unit.styles[i]?.styles, state);
						style = await config.sassConversionMethod(style);
						if (style?.length) {
							newUnit = {
								...newUnit,
								styles: newUnit.styles + style
							};
						}
					} catch (e) {
						console.warn("Couldn't parse SASS", e);
					}
				}
			} else {
				try {
					// console.log('Converting SASS', unit.styles);
					let style = MergeTags.applyMergeTagsToString(unit.styles, state);
					newUnit = {
						...newUnit,
						styles: await config.sassConversionMethod(style)
					};
				} catch (e) {
					console.warn("Couldn't parse SASS: " + unit.name, e);
				}
			}

			// Check if any customization values have a `styles` property and apply them.
			for (let customization of visibleCustomizations) {
				let value = customization.value;
				let styles = value?.['styles'] || value?.['mappings']?.styles || '';

				// Check for creative units with styles as well and add them to the end of the styles string.
				if (value?.['creativeUnits']?.length) {
					// console.log('Creative Unit Styles', value?.['creativeUnits']);
					const unitOverrides = value?.['creativeUnits'].find((u: CreativeUnitDto) => u.id === newUnit.id || u.creativeUnitId === newUnit.creativeUnitId || u.name === newUnit.name);
					if (unitOverrides?.styles) {
						// console.log('Found unit override styles', unitOverrides);
						styles += unitOverrides.styles;
					}
				}

				if (styles?.length) {
					// console.log('Applying Customization Styles', styles);
					try {
						let style = MergeTags.applyMergeTagsToString(styles, state);
						style = await config.sassConversionMethod(style);
						if (style?.length) {
							newUnit = {
								...newUnit,
								styles: newUnit.styles + style
							};
						}
					} catch (e) {
						console.warn("Couldn't parse SASS", e);
					}
				}
			}

			// Remove tab characters and new lines from the styles.
			newUnit = {
				...newUnit,
				styles: newUnit.styles?.replace(/\t/g, '').replace(/\n/g, '')
			};
		}

		// Add the merged state variables to the unit.
		newUnit = {
			...newUnit,
			variables: {
				...state.variables
			}
		};

		// console.log('Converted Unit', newUnit, newUnit.containerClasses, state);

		return {
			...newUnit,
			containerClasses: MergeTags.applyMergeTagsToString(newUnit.containerClasses || '', state),
			customizations: undefined,
			package: undefined,
			layers: newLayers
		} as CreativeUnitDto;
	}

	/**
	 * Creates a blank Creative Unit.
	 */
	public static createCreativeUnit(
		overrides: any,
		layers?: LayerDto[],
		customizations?: CustomizationItemDto[],
		styles?: CreativeUnitWithCustomizationsDto['styles'],
		variables?: CreativeUnitWithCustomizationsDto['variables'],
		theme?: CreativeUnitWithCustomizationsDto['theme']
	): CreativeUnitWithCustomizationsDto {
		let schema: CreativeUnitWithCustomizationsDto = {
			id: overrides?.id || new Date().getTime().toString() + Math.random().toString(36).substring(2, 9),
			name: overrides.name || 'New Creative Unit',
			dimensions: {
				width: overrides.width || 300,
				height: overrides.height || 250,
				ppi: overrides.ppi,
				defaultDisplayUnit: overrides.defaultDisplayUnit || 'px'
			},
			layers: [],
			customizations: [],
			...overrides
		};

		if (layers) {
			schema = {
				...schema,
				layers: layers
			};
		}

		if (customizations) {
			schema = {
				...schema,
				customizations: customizations
			};
		}

		if (styles) {
			schema = {
				...schema,
				styles: styles
			};
		}

		if (theme) {
			schema = {
				...schema,
				theme: { ...theme }
			};
		}

		if (variables) {
			schema = {
				...schema,
				variables: variables
			};
		}

		return schema;
	}

	/**
	 * Apply mappings to a creative unit.
	 * This will apply the mappings to the root and layers of the creative unit.
	 * creativeUnitOverrides lets us know if we need to place things like variables in other places
	 */
	public static applyMappingsToCreativeUnit(
		creativeUnit: CreativeUnitWithCustomizationsDto,
		mappings: CustomizationOptionMappingDto,
		mergeState: any,
		pkg: CreativeUnitPackageDto,
		creativeUnitOverrides?: boolean
	) {
		// Check for variable overrides.
		if (mappings.variables) {
			// Just in case, lets get merge properties.
			let mappingsVariables = MergePropertiesUtils.addVariablesToStateRecursively(
				ObjectUtils.unfreeze(mappings.variables),
				mergeState
			);

			// If mappings is a string, try to parse back to object.
			if (typeof mappingsVariables === 'string') {
				try {
					mappingsVariables = JSON.parse(mappingsVariables);
				} catch (e) {
					console.warn('Unable to parse mappings variables', e);
				}
			}

			if (creativeUnitOverrides) {
				let creativeUnitVariables= ObjectUtils.unfreeze(creativeUnit.variableOverrides || {});

				creativeUnit.variableOverrides = merge(creativeUnitVariables, mappingsVariables);
			} else {
				let creativeUnitVariables= ObjectUtils.unfreeze(creativeUnit.variables || {});
				creativeUnit.variables = merge(creativeUnitVariables, mappingsVariables);
			}

		}

		if (mappings.transformUnit && mappings.transformUnit.enabled && mappings.transformUnit.creativeUnitId !== creativeUnit.creativeUnitId) {
			// Find the creative unit in the package for the target unit.
			const targetUnit = pkg?.creativeUnits?.find(cu => cu.id === mappings.transformUnit.creativeUnitId);
			if (targetUnit) {
				creativeUnit.dimensions = cloneDeep(targetUnit.dimensions);
				creativeUnit.creativeUnitId = targetUnit.id;
				creativeUnit.layers = targetUnit.layers;
				creativeUnit.styles = targetUnit.styles;
			}

		}

		if (mappings.styles){
			if (creativeUnit?.styles) {
				if (Array.isArray(creativeUnit.styles)) {
					creativeUnit.styles = [...creativeUnit.styles, {styles: mappings.styles}];
				} else {
					creativeUnit.styles += mappings.styles;
				}
			}
		}


		if (mappings.layers) {
			// Try to apply mappings across unit layers.
			creativeUnit.layers = LayersUtils.mapAllLayers(
				creativeUnit.layers,
				(layer) => CustomizationDto.applyMappingsToLayers(layer, mappings, undefined, creativeUnit)
			);
		}

		if (mappings.customizations?.length) {
			// console.log('Applying Customization Overrides', mappings.customizations, creativeUnit.customizations);
			const overridesMap = new Map<string, CustomizationOverrideDto>();
			for (const override of mappings.customizations) {
				if (!override?.id) {
					continue;
				}
				overridesMap.set(override.id, override);
			}

			if (overridesMap.size && creativeUnit.customizations?.length) {
				creativeUnit.customizations = CustomizationUtils.mapAllCustomizations(
					creativeUnit.customizations,
					(customization) => {
						const override = overridesMap.get(customization.id);
						if (!override) {
							return customization;
						}

						const updatedCustomization = {
							...customization
						};

						if (Object.prototype.hasOwnProperty.call(override, 'value')) {
							// Find the customizations latest value from the options if it exists.
							const value = updatedCustomization.options?.find(o => isEqual(o.value, override.value)) || override.value;

							updatedCustomization.value = cloneDeep(value);
						}

						if (Object.prototype.hasOwnProperty.call(override, 'dirty')) {
							updatedCustomization.dirty = override.dirty;
						}

						if (Object.prototype.hasOwnProperty.call(override, 'label') && override.label !== undefined) {
							updatedCustomization.label = override.label;
						}

						return updatedCustomization;
					}
				);
			}

			// console.log('Applied Customization Overrides', creativeUnit.customizations);
		}

		return creativeUnit;
	}

	/**
	 * Get a layer from a creative unit by its ID.
	 */
	public static getLayerFromId(creativeUnit: CreativeUnitDto, layerId: LayerDto['id']) {
		return creativeUnit.layers.find(layer => layer.id === layerId);
	}

	/**
	 * Strip all animations from an ad unit.
	 */
	public static stripAnimationsFromUnit(unit: CreativeUnitDto) {
		const newConfig = {
			...unit,
			layers: unit.layers?.map(layer => this.stripAnimationsFromLayer(layer)),
			animation: undefined
		};

		return newConfig;
	}

	/**
	 * Strip grid layer from an ad unit.
	 */
	public static removeGridFromUnit(unit: CreativeUnitDto) {
		return {
			...unit,
			layers: unit?.layers?.filter(layer => layer.type !== LayerType.MOCK && layer.id !== '__GRID_LAYER'),
		};
	}

	/**
	 * Strip any layers that have the property `hideFromPreview` set to true.
	 */
	public static stripHiddenLayersFromUnit(unit: CreativeUnitDto) {
		const newConfig = {
			...unit,
			layers: LayersUtils.filterAllLayers(unit.layers, layer => layer.hideFromPreview !== true)
		};

		return newConfig;
	}

	/**
	 * Apply preview optimizations to the media layers of a creative unit.
	 */
	public static applyPreviewOptimizations(unit: CreativeUnitDto) {
		return {
			...unit,
			layers: LayersUtils.mapAllLayers(unit.layers, layer => {
				if (layer.previewOptimizations) {
					// console.log('Applying Preview Optimizations', layer.previewOptimizations, layer.optimizations);
					// console.log('Merged Optimizations', merge(layer.optimizations, layer.previewOptimizations));
					return {
						...layer,
						optimizations: merge(layer.optimizations, layer.previewOptimizations)
					};
				} else {
					return layer;
				}
			})
		};
	}

	/**
	 * Strip all animations from a layer.
	 */
	public static stripAnimationsFromLayer(layer: LayerDto) {
		let newLayer: LayerDto = {
			...layer,
			animation: undefined
		};

		if (layer.type === 'video') {
			newLayer = {
				...newLayer,
				video: {
					...layer.video,
					autoPlay: false,
					loop: false
				}
			} as LayerDto;
		}

			   if (
				   layer.type === LayerType.GROUP
			   ) {
				   newLayer.layers = LayersUtils.mapAllLayers(layer.layers, subLayer => {
					   return this.stripAnimationsFromLayer(subLayer);
				   });
			   }
  /**
   * Ungroups a SelectionGroup layer in the creative unit, moving its sublayers to the root and removing the group.
   * @param creativeUnit The creative unit to modify.
   * @param selectionGroupId The ID of the SelectionGroup to ungroup.
   * @returns The updated creative unit with the group removed and its layers promoted to root.
   */

		return newLayer;
	}

	/**
	 * Update a customization's value in an ad unit.
	 * This DOES NOT update the actual ad unit values, it only updates the customization values.
	 */
	public static updateCustomizationValue(
		creativeUnit: CreativeUnitWithCustomizationsDto,
		customizationId: string,
		value: any,
		dirty: boolean,
		mergeTagState: any,
		useCreativeUnitValue?: boolean
	): CreativeUnitWithCustomizationsDto {
		let newUnit: CreativeUnitWithCustomizationsDto = { ...creativeUnit };
		let customization = CustomizationUtils.findAllCustomizations(newUnit.customizations)?.find(c => c.id === customizationId);
		let customizationClass: typeof CustomizationDto;
		let matchedValue; // The value that was matched from the customization.

		// Check if the root layer has the customization.
		// console.log('Customization!', customizationId, customization, newUnit.customizations);
		if (customization) {
			customizationClass = getCustomizationClassByType(customization.type);

			if (useCreativeUnitValue) {
				matchedValue = customizationClass?.findMatchedValue(customization, value) ?? null;
			}

			// Test that the value is valid.
			let newValue = matchedValue || value;
			if (newValue?.visibilityConditions?.length) {
				console.log('Value has visibility conditions', newValue, customization, mergeTagState);

				// Evaluate the visibility conditions.
				const evaluation = Evaluate.evaluateMultiple(newValue.visibilityConditions, mergeTagState);

				if (!evaluation) {
					console.log('Value is invalid', newValue);

					// Check if there are any other options that have the same value but are valid.
					if (customization.type === CustomizationType.SELECT) {

						// Get the valid options for this customization.
						let visibleOptions = customization?.options?.filter(option => {
							// Check if the option has visibility conditions.
							if (option.visibilityConditions?.length) {
								// Evaluate the visibility conditions.
								return Evaluate.evaluateMultiple(option.visibilityConditions, mergeTagState);
							} else {
								return true;
							}
						});

						// Now figure out if there are any valid options that have the same value.
						let validOptions = visibleOptions?.filter(option => {
							// Check if the option has the same value.
							// console.log('Checking option', option.value, newValue.value);
							return isEqual(option.value, newValue.value);
						});
						// console.log('Checking other options for valid values', visibleOptions, validOptions);

						if (validOptions?.length) {
							// Cool! Find the first valid option and use that value.
							newValue = validOptions[0];
						} else {
							// If we found no options, let's just not change anything and return.
							return newUnit;
						}
					} else {
						return newUnit;
					}

				}
			}

			// Update the customization to make sure the new values are set.
			newUnit = {
				...newUnit,
				customizations: CustomizationUtils.mapAllCustomizations(newUnit.customizations, c => {
					if (c.id === customizationId) {
						return {
							...c,
							value: newValue,
							dirty: dirty !== null ? dirty : c.dirty,
						};
					} else {
						return c;
					}
				}) //arrayUpdate(newUnit.customizations, customizationId, ({ ...customization, value: matchedValue || value } as any))
			};
		}

		return newUnit;
	}

	/**
	 * Updates an ad unit based on the current values of several customizations.
	 * Returns a new ad unit with the updated values.
	 * @param {CreativeUnitWithCustomizations} creativeUnit - The ad unit to update.
	 * @param {CreativeUnitCustomization[]} customizations - The customizations to update.
	 * @return {CreativeUnitWithCustomizations} A new ad unit with the updated values.
	 **/
	public static updateCreativeUnitWithCustomizationsValues(
		creativeUnit: CreativeUnitDto,
		customizations: CustomizationItemDto[]
	): CreativeUnitDto {
		let newUnit: CreativeUnitWithCustomizationsDto = { ...creativeUnit };

		// Iterate through the customizations and update the ad unit.
		// console.log('Updating customizations', customizations);
		customizations.forEach(customization => {
			// console.log('Updating unit with customization', customization.label, customization);
			newUnit = this.updateCreativeUnitWithCustomizationValue(newUnit, customization.id, customization.value);
			// console.log('Updated unit from customization', newUnit);
		});

		return newUnit;
	}

	/**
	 * Updates the values of an ad unit based on a customization value.
	 * Returns a new ad unit with the updated customization.
	 *
	 * @param {CreativeUnitWithCustomizations} creativeUnit - The ad unit to update.
	 * @param {string} customizationId - The ID of the customization to update.
	 * @param {any} value - The new value for the customization.
	 * @param {boolean} [useCreativeUnitValue] - If true, use the value from the ad unit instead of the value passed in.  Useful for updating multiple units.
	 * @return {CreativeUnitWithCustomizations} A new ad unit with the updated customization.
	 */
	public static updateCreativeUnitWithCustomizationValue(
		creativeUnit: CreativeUnitWithCustomizationsDto,
		customizationId: string,
		value: any,
		useCreativeUnitValue?: boolean
	): CreativeUnitWithCustomizationsDto {
		let newUnit: CreativeUnitWithCustomizationsDto = { ...creativeUnit };
		let customization = CustomizationUtils.findAllCustomizations(newUnit.customizations)?.find(c => c.id === customizationId);
		let customizationClass: typeof CustomizationDto;
		let matchedValue; // The value that was matched from the customization.

		// Check if the root layer has the customization.
		if (customization) {
			customizationClass = getCustomizationClassByType(customization.type);

			if (useCreativeUnitValue) {
				matchedValue = customizationClass?.findMatchedValue(customization, value) ?? null;
			}
		}

		// `customization.type === 'video-picker'` is a workaround to return the creativeUnit
        // without assigning a wrong assetPath to its layers in case of a video picker customization
        // this is related to the bug #6389
		if (!this.customizationHasValue(customization)) {
			return newUnit;
		}

		// Get all the applicable layers for the customization and update them.
		let layers = CreativeUnitUtils.getLayersFromCustomization(customization, (newUnit.layers)) || [];
		for (let layer of layers) {
			// Find customization in layer.
			customizationClass = getCustomizationClassByType(customization.type);

			if (useCreativeUnitValue) {
				matchedValue = customizationClass?.findMatchedValue(customization, value) ?? null;
			}

			// Update the layer based on the customization type.
			layer = customizationClass?.updateLayer(layer, customization, matchedValue || value, newUnit) || layer;

			newUnit = {
				...newUnit,
				layers: LayersUtils.mapAllLayers(newUnit.layers, l => (l.id === layer.id ? layer : l))
			};
		}

		// Support updating other layers if the customization value has a `layers` property or the customization has a mappings layers property.
		if (value?.layers || customization?.mappings || value?.creativeUnits?.length) {
			newUnit = {
				...newUnit,
				layers: customizationClass?.updateLayers(newUnit.layers, customization, matchedValue || value, newUnit) || newUnit.layers
			};
		}

		return newUnit;
	}

	private static customizationHasValue(customization: CustomizationItemDto): boolean {
		if (customization == null) return false;
		if (customization.value == null) return false;

		if (typeof customization.value === 'object') {
			const value = ObjectUtils.cleanObject(ObjectUtils.unfreeze(customization.value));
			return Object.keys(value).length > 0;
		}

		return true;
	}

	/**
	 * Gets all the layers that a customization applies to, supporting layer customizations or root customizations.
	 * @param {CreativeUnitCustomization} customization - The customization to check.
	 * @param {CreativeUnitWithCustomizations} creativeUnit - The ad unit to check.
	 **/
	public static getLayersFromCustomization(customization: CustomizationItemDto, layers: LayerDto[]): LayerDto[] {
		let applicableLayers = [];

		// Check if the customization is in a layer and update it.  ** DEPRECATED **
		let layer = LayersUtils.findAllLayers(layers, l => l.customizations?.find(c => c.id === customization.id))?.[0];
		if (layer) {
			applicableLayers.push(layer);
		}

				// Check if the customization explictly has applicable layers.  Include nested frame layers
		if (customization.layerIds) {
			LayersUtils.findAllLayers(layers, l => customization.layerIds?.includes(l.id))?.forEach(l => applicableLayers.push(l));
		}

		// console.log('Applicable Layers', applicableLayers)
		// Check if the customization has specific creative unit overrides and look through all of those for applicable layers.
		// console.log('Checking Creative Units', customization.name, customization.value);
		// if ((customization as CustomizationSelectDto).value?.creativeUnits) {
		// 	(customization as CustomizationSelectDto).value?.creativeUnits.forEach((unit: CreativeUnitDto) => {
		// 		unit.layers?.forEach(l => {
		// 			applicableLayers.push(l);
		// 		});
		// 	});
		// }

		// Filter out any duplicate layers.
		// applicableLayers = applicableLayers.filter((layer, index, self) => self.findIndex(l => l.id === layer.id) === index);

		return applicableLayers;
	}



	/**
	 * Get the most similar creative unit based on the width and height.
	 * It's likely that no creative unit will match the exact dimensions, so we need to find the closest match.
	 * Adjust the aspect ratio weight to determine how much the aspect ratio should be considered.
	 * A weight of 0.5 means that the aspect ratio and resolution are equally important.
	 * A weight of 1 means that the aspect ratio is the only important factor.
	 * A weight of 0 means that the resolution is the only important factor.
	 */
	public static getMostSimilarCreativeUnit(
		creativeUnits: CreativeUnitDto[],
		width: string,
		height: string,
		aspectRatioWeight: number = 1
	) {
		const widthInt = parseInt(width);
		const heightInt = parseInt(height);
		const targetAspectRatio = widthInt / heightInt;
		const targetResolution = widthInt * heightInt;

		if (!creativeUnits.length) return undefined;

		// console.log('Find a similar creative unit', width, height, targetAspectRatio, targetResolution, creativeUnits);
		return creativeUnits.reduce((prev, curr) => {
			const prevAspectRatio = prev.dimensions.width / prev.dimensions.height;
			const currAspectRatio = curr.dimensions.width / curr.dimensions.height;

			const prevResolution = prev.dimensions.width * prev.dimensions.height;
			const currResolution = curr.dimensions.width * curr.dimensions.height;

			const prevAspectRatioDifference = Math.abs(targetAspectRatio - prevAspectRatio);
			const currAspectRatioDifference = Math.abs(targetAspectRatio - currAspectRatio);

			const prevResolutionDifference = Math.abs(targetResolution - prevResolution);
			const currResolutionDifference = Math.abs(targetResolution - currResolution);

			const prevTotalDifference = aspectRatioWeight * prevAspectRatioDifference + (1 - aspectRatioWeight) * prevResolutionDifference;
			const currTotalDifference = aspectRatioWeight * currAspectRatioDifference + (1 - aspectRatioWeight) * currResolutionDifference;

			// console.log('Comparing Creative Units', prev.name, prevAspectRatio, prevAspectRatioDifference, prevTotalDifference, curr.name, currAspectRatio, currAspectRatioDifference, currTotalDifference);

			return prevTotalDifference < currTotalDifference ? prev : curr;
		});
	}

	/**
	 * Get the container classes for a creative unit.  This combines the unit and package classes and sometimes appends the unit classes.
	 */
	public static getContainerClasses(unitClasses: string, packageClasses: string, appendContainerClasses: boolean) {
		if (appendContainerClasses) {
			// Combine unit and package css classes, if they exist, then remove any duplicates.  Classes are separated by commas.
			let classes = [packageClasses, unitClasses].filter(c => c).join(',').split(',').map(c => c.trim());
			classes = [...new Set(classes)];
			return classes.join(',');
			return
		} else {
			return unitClasses || packageClasses;
		}
	}

	/**
	 * Convert a creative unit's dimensions to a new unit size.
	 */
	public static convertToNewUnitSize(
		oldDimensions: CreativeUnitWithCustomizationsDto['dimensions'],
		newUnit: CreativeUnitDimensionsUnit
	): CreativeUnitWithCustomizationsDto['dimensions'] {
		let dimensions = {
			...oldDimensions,
			defaultDisplayUnit: newUnit || oldDimensions.defaultDisplayUnit
		};

		let updated = DimensionUtils.convert(
			oldDimensions.width,
			oldDimensions.height,
			this.creativeUnitDimensionsUnitToDimension(oldDimensions.defaultDisplayUnit),
			this.creativeUnitDimensionsUnitToDimension(dimensions.defaultDisplayUnit),
			oldDimensions.ppi
		);

		// console.log('Updated unit size', updated);

		return {
			...dimensions,
			width: updated?.width ? Number(Number(updated.width).toFixed(4)) : undefined,
			height: updated?.height ? Number(Number(updated?.height).toFixed(4)) : undefined,
			ppi: updated?.originalScale ? Number(updated?.originalScale) : undefined,
		};
	}

	public static creativeUnitDimensionsUnitToDimension(unit: CreativeUnitDimensionsUnit | string) {
		switch(unit) {
			case CreativeUnitDimensionsUnit.INCHES:
				return Dimension.Inches;
			case CreativeUnitDimensionsUnit.CENTIMETERS:
				return Dimension.Centimeters;
			case CreativeUnitDimensionsUnit.MILLIMETERS:
				return Dimension.Millimeters;
			case CreativeUnitDimensionsUnit.POINTS:
				return Dimension.Points;
			case CreativeUnitDimensionsUnit.PICAS:
				return Dimension.Picas;
			case CreativeUnitDimensionsUnit.PIXELS:
			default:
				return Dimension.Pixels;
		}
	}

	public static dimensionToCreativeUnitDimensionsUnit(dimension: Dimension) {
		switch(dimension) {
			case Dimension.Inches:
				return CreativeUnitDimensionsUnit.INCHES;
			case Dimension.Centimeters:
				return CreativeUnitDimensionsUnit.CENTIMETERS;
			case Dimension.Millimeters:
				return CreativeUnitDimensionsUnit.MILLIMETERS;
			case Dimension.Points:
				return CreativeUnitDimensionsUnit.POINTS;
			case Dimension.Picas:
				return CreativeUnitDimensionsUnit.PICAS;
			case Dimension.Pixels:
			default:
				return CreativeUnitDimensionsUnit.PIXELS;
		}
	}

	public static convertUnitToListing(unit: CreativeUnitDto): CreativeUnitListingDto {
		return {
			id: unit.id,
			name: unit.name,
			description: unit.description,
			dimensions: unit.dimensions,
			thumbnail: unit.thumbnail,
			hidden: unit.hidden,
			hideSize: unit.hideSize,
			linkedCreativeUnitId: unit.id,
			creativeUnitIds: [unit.id],
			locale: unit.locale,
			metadata: unit.metadata
		}
	}

	public static getNextIndex(names: string[], baseName: string): number {
		let nextHighestIndex = 2; // Starting index

		// extract indexes from the names array
		const indexes = names
			.filter(name => name.startsWith(baseName + ' - ')) // only names with the baseName prefix
			.map(name => {
				const parts = name.split(' - ');
				const digit = parseInt(parts[1]);
				return isNaN(digit) ? null : digit; // extract numeric part if it exists
			})
			.filter(digit => digit !== null)
			.sort((a, b) => a - b); // sort in ascending order

		// find the smallest missing index
		const findSmallestMissing = (indexes: number[], start: number): number => {
			for (let i = 0; i < indexes.length; i++) {
				if (indexes[i] > start) {
					return start;
				}
				start++;
			}
			return start;
		};

		nextHighestIndex = findSmallestMissing(indexes, nextHighestIndex);
		return nextHighestIndex;
	}

	/** When style is duplicated, find the first comment matching the pattern and remove it **/
	public static sanitizeDuplicatedStyles(styles: any): string | StylesConfigDto[] {
		const stripComment = (s: string): string => {
			return s.replace(/^\/\*.*?\*\/[\n]?/, '');
		};

		if (!styles) {
			return '';
		}
		if (typeof styles === 'string') {
			return stripComment(styles);
		}

		// Check if styles is an array before calling map
		if (Array.isArray(styles)) {
			return styles.map(style => {
				return {
					...style,
					styles: stripComment(style.styles)
				}
			}) as StylesConfigDto[];
		}

		// If styles is neither string nor array, return empty string as fallback
		console.warn('sanitizeDuplicatedStyles: Invalid styles type, expected string or array but got:', typeof styles, styles);
		return '';
	}

	/**
	 * Merge Linked Layers with unit layers by ID, to account for unit-specific overrides and additional layers.
	 * @param unitLayers
	 * @param packageLayers
	 */
	public static mergePackageLayersWithUnitLayers(
		packageLayers: LayerDto[],
		unitLayers: LayerDto[],
	): LayerDto[] {
		if (!unitLayers?.length) {
			return packageLayers || [];
		}

		const flatPkgLayers = LayersUtils.flattenLayers(packageLayers || []);
		const pkgLayerMap: Map<string, LayerDto> = new Map(flatPkgLayers.map(layer => [layer.id, layer]));

		return LayersUtils.mapAllLayers([...unitLayers], unitLayer => {
			// Find the matching package layer by ID.
			const pkgLayer = pkgLayerMap.get(unitLayer.id);

			if (pkgLayer) {
				// If a package layer match exists, merge them.
				const isGroup = unitLayer.type === 'group';

				// prevent lodash from array-merging children
				const unitForMerge = isGroup
					? { ...unitLayer, layers: undefined }
					: unitLayer;

				const mergedLayer = merge(cloneDeep(pkgLayer), cloneDeep(unitForMerge)) as LayerDto;

				// For certain properties, prefer taking on the entirety of the unit layer's value.
				mergedLayer.animation = unitLayer.animation || pkgLayer.animation;
				mergedLayer.mergeTags =  unitLayer.mergeTags || pkgLayer.mergeTags;

				// for groups, prefer unit children wholesale
				if (isGroup) {
					mergedLayer.layers = cloneDeep(unitLayer.layers ?? []);
				}

				return mergedLayer;
			} else {
				// If there is no match, then the layer is unit-specific: return the unit layer as-is.
				return unitLayer;
			}
		});
	}

	/**
	 * Generate creative unit variations using an array of different customization keyvalue sets.
	 * @param variationsById - an array of different customization keyvalue sets, where each entry in the array is a different variation setup.
	 * @param units - the units to apply the variations to. Each unit will be duplicated for each variation.
	 */
	public static generateCreativeUnitVariations(
		variationsById: { [customizationId: string]: any }[],
		units: CreativeUnitWithCustomizationsDto[]
	): CreativeUnitWithCustomizationsDto[] {

		const variatedUnits: CreativeUnitWithCustomizationsDto[] = [];

		const allCustomizations = CustomizationUtils.flattenCustomizations(units[0]?.customizations) ?? [];


		const getType = (customizationId: any) => {
			return allCustomizations.find(c => c.id === customizationId)?.type;
		}
		const variationName = (variation: Record<string, any>) =>
			(Object.entries(variation)
				.map(([k,v]: any) => CustomizationDto.getValueOrDefault(getType(k), v))
				.filter(v => v != null && v !== "")
				.join(" + "));

		for (const variation of variationsById) {

		  	// one group per unique variation (by values)
			let group = undefined;
		  	const groupId = `${Date.now()}${Math.random().toString(36).slice(2, 9)}`;

			const variationValues  = variationName(variation);
			if (units?.length > 1) {
				group = { id: groupId, instanceId: groupId, name: variationValues };
			}

			for (const unit of units) {
				let unitName = unit.name;
				if(group === undefined){
					unitName = variationValues;
				}
				variatedUnits.push({
				...unit,
				name: unitName,
				customizations: CustomizationUtils.mapAllCustomizations(
					unit.customizations,
					(customization) => {
					const assigned = (variation as any)[customization.id];

					if (assigned !== undefined) {
						return { ...customization, value: customization.type === CustomizationType.TEXT ? assigned.value : assigned, dirty: true };
					}
					return { ...customization, value: customization.value };
					}
				),
				group, // reuse same group object for all units with this variation
				});
			}
		}
		return variatedUnits;
	  }

	  /**
	   * Find a creative unit in a list of units (and their nested items) that matches a given function.
	   * @param units - The list of creative units to search through.
	   * @param func - A function that takes a creative unit and returns true if it matches the desired criteria.
	   * @returns The first creative unit that matches the criteria or null if none is found.

	   */
	public static findCreativeUnit(units: CreativeUnitWithCustomizationsDto[], func: Function): CreativeUnitWithCustomizationsDto {
		for (const unit of units) {
			if (func(unit)) {
				return unit;
			}
			if (unit.items?.length) {
				const found = this.findCreativeUnit(unit.items, func);
				if (found) {
					return found;
				}
			}
		}
		return null;
	}

	/**
	 * Find a creative unit by its ID in a list of units (and their nested items). This is shorthand for findCreativeUnit.
	 * @param units - The list of creative units to search through.
	 * @param id - The ID of the creative unit to find.
	 * @returns The creative unit with the specified ID or null if none is found.
	 */
	public static findCreativeUnitById(units: CreativeUnitWithCustomizationsDto[], id: string): CreativeUnitWithCustomizationsDto {
		return this.findCreativeUnit(units, (unit: CreativeUnitWithCustomizationsDto) => unit.id === id);
	}

	/**
	 * Find all creative units in a list of units (and their nested items) that match a given function.
	 * @param units - The list of creative units to search through.
	 * @param func - A function that takes a creative unit and returns true if it matches the desired criteria.
	 * @returns An array of all creative units that match the criteria.
	 */

	public static findAllCreativeUnit(units: CreativeUnitWithCustomizationsDto[], func: Function): CreativeUnitWithCustomizationsDto[] {
		return (units || []).reduce<CreativeUnitWithCustomizationsDto[]>((acc, unit) => {
			if (func(unit)) {
				acc.push(unit);
			}

			if (unit.items?.length) {
				acc = acc.concat(this.findAllCreativeUnit(unit.items, func));
			}

			return acc;
		}, []);
	}

}
